import React from 'react';

import { Clear } from '@mui/icons-material';
import { Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

import { PRIMARY_GRAY } from 'commons/styles/colors';
import { IInboundInvoice } from 'commons/types';

import S from '../InboundList.styles';

interface IInboundListInvoiceEditProps {
  displayedInboundInvoice: IInboundInvoice;
  setDisplayedInboundInvoice: (value: IInboundInvoice) => void;
  setUpdatedDataType: (type: 'invoice_number' | 'invoice_date') => void;
  setUpdatedData: (data: string) => void;
  isEditingInvoiceNumber: boolean;
  isEditingInvoiceDate: boolean;
  setIsEditingInvoiceNumber: (value: boolean) => void;
  setIsEditingInvoiceDate: (value: boolean) => void;
  setOpenModal: (value: boolean) => void;
}

export const InboundListInvoiceEditForm: React.FC<
  IInboundListInvoiceEditProps
> = ({
  displayedInboundInvoice,
  setDisplayedInboundInvoice,
  setUpdatedDataType,
  setUpdatedData,
  isEditingInvoiceNumber,
  isEditingInvoiceDate,
  setIsEditingInvoiceNumber,
  setIsEditingInvoiceDate,
  setOpenModal,
}) => {
  const onInboundInvoiceFieldChange = (
    type: keyof Pick<IInboundInvoice, 'invoice_number' | 'invoice_date'>,
    value: string,
  ): void => {
    const clonedState = structuredClone(displayedInboundInvoice);

    clonedState[type] = value;
    setDisplayedInboundInvoice(clonedState);
  };

  const onClickSave = (
    type: 'invoice_number' | 'invoice_date',
    value: string,
  ): void => {
    setUpdatedDataType(type);
    setUpdatedData(value);
    setOpenModal(true);
  };

  return (
    <Grid container columnSpacing={3} rowSpacing={1}>
      <Grid item container xs={12} md={6} alignItems="center">
        <Grid item xs={3} md={2} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
          Invoice Number:
        </Grid>
        <Grid item xs={6} md={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="-"
            value={displayedInboundInvoice.invoice_number}
            disabled={!isEditingInvoiceNumber}
            onChange={(event) =>
              onInboundInvoiceFieldChange('invoice_number', event.target.value)
            }
            id="field-invoice-number-inbound-list"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={!isEditingInvoiceNumber}
                    onClick={() =>
                      onInboundInvoiceFieldChange('invoice_number', '')
                    }
                  >
                    <Clear style={{ color: PRIMARY_GRAY }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { paddingRight: 0 },
            }}
          />
        </Grid>
        <S.ButtonGrid item xs={3} md={2} textAlign="right">
          {isEditingInvoiceNumber ? (
            <S.PrimaryButton
              disabled={!displayedInboundInvoice.invoice_number}
              onClick={() =>
                onClickSave(
                  'invoice_number',
                  displayedInboundInvoice.invoice_number,
                )
              }
            >
              SAVE
            </S.PrimaryButton>
          ) : (
            <S.SecondaryButton onClick={() => setIsEditingInvoiceNumber(true)}>
              EDIT
            </S.SecondaryButton>
          )}
        </S.ButtonGrid>
      </Grid>
      <Grid item container xs={12} md={6} alignItems="center">
        <Grid item xs={3} md={2} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
          Invoice Date:
        </Grid>
        <Grid item xs={6} md={8}>
          <DatePicker
            format="dd/MM/yyyy"
            defaultValue={null}
            value={
              displayedInboundInvoice.invoice_date
                ? DateTime.fromISO(displayedInboundInvoice.invoice_date)
                : null
            }
            disabled={!isEditingInvoiceDate}
            onChange={(value) => {
              onInboundInvoiceFieldChange(
                'invoice_date',
                value?.toString() || '',
              );
            }}
            slotProps={{
              textField: {
                id: 'field-invoice-date-inbound-list',
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={!isEditingInvoiceDate}
                        onClick={() => {
                          onInboundInvoiceFieldChange('invoice_date', '');
                        }}
                      >
                        <Clear style={{ color: PRIMARY_GRAY }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { paddingRight: 0 },
                },
              },
            }}
          />
        </Grid>
        <S.ButtonGrid item xs={3} md={2} textAlign="right">
          {isEditingInvoiceDate ? (
            <S.PrimaryButton
              disabled={
                !displayedInboundInvoice.invoice_date ||
                Number.isNaN(
                  new Date(displayedInboundInvoice.invoice_date).getTime(), // check date validity
                )
              }
              onClick={() =>
                onClickSave(
                  'invoice_date',
                  DateTime.fromISO(
                    displayedInboundInvoice.invoice_date,
                  ).toFormat('dd/LL/yyyy'),
                )
              }
            >
              SAVE
            </S.PrimaryButton>
          ) : (
            <S.SecondaryButton onClick={() => setIsEditingInvoiceDate(true)}>
              EDIT
            </S.SecondaryButton>
          )}
        </S.ButtonGrid>
      </Grid>
    </Grid>
  );
};
